// Hook reutilizable del canal de eventos en vivo del HCE (Sala CRM<->HCE, rebanada 3). Ver lib/eventosSala.ts para el diseño.
//
//   useEventosSala((motivo) => void recargar(), { tipos: ['calendario'] })
//
// `alCambio` se llama cuando:
//   - llega un aviso («sala» o «calendario», filtrado por `tipos`; en cada pestaña, la líder lo recibe del servidor y las demás por
//     BroadcastChannel, así que cada pestaña recarga SUS datos);
//   - el canal vuelve a abrirse tras una caída («reconexion»): recupera lo que pasó mientras estuvo cortado;
//   - pasa el respaldo periódico de 30 s («respaldo»), solo con la pestaña visible, y al volver a verla: es la red de seguridad si el canal
//     se cayó o un intermediario lo retiene. El reloj del cliente no decide nada clínico: solo dispara una recarga.
// El aviso no trae datos: quien lo usa vuelve a pedir lo suyo por sus rutas autenticadas de siempre.
import { useEffect, useRef, useState } from 'react';
import { cabecerasBoveda } from './audit/auditedFetch';
import {
  crearConexionEnVivo,
  crearEventosEntrePestanas,
  pedirTicketHce,
  type EstadoEnVivo,
  type FuenteEventos,
  type TipoEventoSala,
} from './eventosSala';
import { vaultUrl } from './hceVault';

export type MotivoRecarga = TipoEventoSala | 'reconexion' | 'respaldo';

export const RESPALDO_MS = 30_000;

export type OpcionesUseEventosSala = {
  /** Qué avisos interesan (por defecto los dos). */
  tipos?: readonly TipoEventoSala[];
  enabled?: boolean;
  respaldoMs?: number;
};

export function useEventosSala(alCambio: (motivo: MotivoRecarga) => void, opciones: OpcionesUseEventosSala = {}): { estado: EstadoEnVivo; esLider: boolean } {
  const { enabled = true, respaldoMs = RESPALDO_MS } = opciones;
  const tiposClave = (opciones.tipos ?? ['sala', 'calendario']).join(',');
  const [estado, setEstado] = useState<EstadoEnVivo>('conectando');
  const [esLider, setEsLider] = useState(false);
  // Siempre el último callback, sin reiniciar la conexión en cada render.
  const ultimo = useRef(alCambio);
  ultimo.current = alCambio;

  useEffect(() => {
    if (!enabled) return;
    const tipos = tiposClave.split(',') as TipoEventoSala[];
    let previo: EstadoEnVivo = 'conectando';
    const conexionEntrePestanas = crearEventosEntrePestanas({
      clave: `lc-eventos-sala:${globalThis.location?.host ?? ''}`,
      bloqueos: typeof navigator !== 'undefined' ? ((navigator as unknown as { locks?: never }).locks ?? null) : null,
      crearCanal: typeof BroadcastChannel !== 'undefined' ? (n) => new BroadcastChannel(n) as never : null,
      crearConexion: (cb) =>
        crearConexionEnVivo({
          pedirTicket: () => pedirTicketHce(vaultUrl('/api/hce/eventos/ticket'), cabecerasBoveda()),
          urlFlujo: (ticket) => vaultUrl(`/api/eventos/stream?ticket=${encodeURIComponent(ticket)}`),
          crearFuente: (url) => new EventSource(url) as unknown as FuenteEventos,
          alCambio: cb.alCambio,
          alEstado: cb.alEstado,
        }),
      alCambio: (tipo) => {
        if (tipos.includes(tipo)) ultimo.current(tipo);
      },
      alEstado: (nuevo) => {
        // Reabrir tras una caída: lo ocurrido mientras estuvo cortado no llegó como aviso, se pide de nuevo.
        if (previo === 'reconectando' && nuevo === 'en-vivo') ultimo.current('reconexion');
        previo = nuevo;
        setEstado(nuevo);
      },
      alLider: setEsLider,
      // Traspaso de líder: lo ocurrido en el hueco no llegó como aviso, se pide de nuevo.
      alTraspaso: () => ultimo.current('reconexion'),
    });
    conexionEntrePestanas.iniciar();

    const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible';
    const temporizador = setInterval(() => {
      if (visible()) ultimo.current('respaldo');
    }, respaldoMs);
    const alVolver = () => {
      if (visible()) ultimo.current('respaldo');
    };
    document.addEventListener('visibilitychange', alVolver);

    return () => {
      conexionEntrePestanas.detener();
      clearInterval(temporizador);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [enabled, tiposClave, respaldoMs]);

  return { estado, esLider };
}
